// ═══════════════════════════════════════════════════════════════
// DeclinedWorkPanel — what this customer has passed on before.
//
// The whole point of recording a "no": when the car is back in the
// driveway six months later, the tech opens the customer and sees
// "they declined rear brakes in March, $340." That conversation is
// worth more than the feature that captured it.
//
// Reads straight off the jobs already subscribed for this customer —
// no extra collection, no extra read.
// ═══════════════════════════════════════════════════════════════
import React, { useMemo, useState } from 'react';
import { History, Camera, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { declinedHistory } from './lib/recommendations.js';
import { formatPrice } from './lib/job-helpers.js';
import { vehicleLabel } from './Vehicles.jsx';

function formatWhen(d) {
  if (!d) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function DeclinedWorkPanel({ jobs, vehicles = [] }) {
  const [open, setOpen] = useState(false);
  const declined = useMemo(() => declinedHistory(jobs), [jobs]);

  if (declined.length === 0) return null;

  const vehicleById = Object.fromEntries(vehicles.map((v) => [v.id, v]));
  const total = declined.reduce((s, d) => s + d.price, 0);

  return (
    <section className="declined-panel">
      <button
        type="button"
        className="declined-head"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <History size={15} />
        <span className="declined-head-text">
          <strong>
            {declined.length} previously declined
            {declined.length === 1 ? ' repair' : ' repairs'}
          </strong>
          <span className="muted small">
            {formatPrice(total)} of work they passed on — worth revisiting
          </span>
        </span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {open && (
        <ul className="declined-list">
          {declined.map((d) => {
            const v = d.vehicleId ? vehicleById[d.vehicleId] : null;
            return (
              <li key={`${d.jobId}-${d.id}`} className={`declined-row rec-${d.urgency}`}>
                <div className="declined-row-head">
                  <span className="declined-label">
                    {d.urgency === 'replace' && <AlertTriangle size={12} />}
                    {d.label}
                  </span>
                  <span className="declined-price mono">{formatPrice(d.price)}</span>
                </div>
                {d.detail && <p className="declined-detail muted small">{d.detail}</p>}
                <div className="declined-row-foot muted small">
                  <span>Declined {formatWhen(d.declinedAt)}</span>
                  {v && <span> · {vehicleLabel(v)}</span>}
                  {d.jobNumber != null && <span> · EST-{d.jobNumber}</span>}
                  {d.photoUrls?.length > 0 && (
                    <span> · <Camera size={11} /> {d.photoUrls.length}</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
